// Search orchestration for the "published versions" modal: a debounced server pass, a
// monotonic stale-response guard, explicit error + retry, and append-style paging.
//
// Modelled on the CRM matches tab's `useMatchesSearch`, with three differences that come
// from what this surface is:
//
// 1. THE SEED ARRIVES FROM THE SERVER. The picker is handed a listing ID and nothing
//    else — not the city, not the price — so it cannot compute the seeded filters, and
//    duplicating the server's price-window policy in the browser would be a second copy
//    of a rule with no test holding the two together. The first pass therefore sends NO
//    `filters`; the response reports the seed it chose, and that becomes the chip row's
//    initial state. Adopting it deliberately does NOT re-fire the pass (see `nonce`).
// 2. THERE IS NO SESSION CACHE. Linking is a write that changes what the next search
//    should say about a row, and the modal is opened rarely and deliberately. A cache
//    here would show a "not linked" row seconds after she linked it.
// 3. PAGING APPENDS. The endpoint's page size is also its ranking window (the sameness
//    score orders the page, not the corpus), so "load more" is genuinely "rank the next
//    page", and the natural rendering is one growing list.
//
// ⚠ `getListingSourceCandidates` comes from WebotServerContext, whose Provider value is
// an un-memoized object literal — its identity changes on every ancestor render. It is
// held in a ref and is NEVER an effect dependency; making it one re-fires the request on
// every parent re-render, which is precisely the bug the old picker had.
import { useCallback, useEffect, useRef, useState } from "react";
import useWebotServer from "hooks/useWebotServer";
import type { MatchFilters } from "components/_dashboard/contacts/detail/tabs/matches/contactMatchFilters";
import type { REST_schema } from "../../../../@types/sharedSchema";
import {
  matchFiltersFromWire,
  seededChipIds,
  wireFiltersFromMatchFilters
} from "./sourceCandidateFilters";
import type { SeededChipId } from "./sourceCandidateFilters";

const SEARCH_DEBOUNCE_MS = 300;

export type SourceCandidatesTarget = Pick<
  REST_schema.ListingSourceCandidatesRequest,
  "listingId" | "draft"
>;

export type SourceCandidateSort = REST_schema.ListingSourceCandidateSort;

export const DEFAULT_CANDIDATE_SORT: SourceCandidateSort = { field: "sameness" };

export type SourceCandidatesSearch = {
  /** Null only before the seed lands — the chip row renders disabled until then. */
  filters: MatchFilters | null;
  /** The seed as adopted, so the caller can tell "unchanged" from "she narrowed it". */
  seedFilters: MatchFilters | null;
  /** Chips currently showing a value the SERVER seeded rather than one she picked. */
  seededChips: SeededChipId[];
  /** The listing's own comparable fields — what the sameness rank is measured against. */
  seed: REST_schema.ListingSourceCandidateSeed | null;
  queryText: string;
  sort: SourceCandidateSort;

  candidates: REST_schema.ListingSourceCandidate[];
  totalHits: number;
  totalHitsCapped: boolean;
  hasMore: boolean;
  /** No area and no free text — nothing was searched. NOT the same as "no results". */
  needsNarrowing: boolean;
  searchTermMode?: "all" | "any" | "none";

  loading: boolean;
  loadingMore: boolean;
  loadedOnce: boolean;
  error: boolean;

  setFilters: (patch: Partial<MatchFilters>) => void;
  /** Throws away her narrowing and asks the server for the listing's seed again. */
  resetToSeed: () => void;
  setQueryText: (query: string) => void;
  setSort: (sort: SourceCandidateSort) => void;
  loadMore: () => void;
  retry: () => void;
};

// NOTE: there is deliberately no `patchCandidate`. A row's linked state is read from the
// FORM's link list, not from the server snapshot the row was fetched with, so a link made
// a second ago is already reflected without rewriting the search results — and the server
// answer stays the server answer.

export function useSourceCandidatesSearch(target: SourceCandidatesTarget): SourceCandidatesSearch {
  const api = useWebotServer();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [filters, setFiltersState] = useState<MatchFilters | null>(null);
  /** The adopted seed, kept so the modal can tell "still as seeded" from "she edited it". */
  const [seedFilters, setSeedFilters] = useState<MatchFilters | null>(null);
  const [seededChips, setSeededChips] = useState<SeededChipId[]>([]);
  const [seed, setSeed] = useState<REST_schema.ListingSourceCandidateSeed | null>(null);
  const [queryText, setQueryText] = useState("");
  const [sort, setSortState] = useState<SourceCandidateSort>(DEFAULT_CANDIDATE_SORT);

  const [candidates, setCandidates] = useState<REST_schema.ListingSourceCandidate[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [totalHitsCapped, setTotalHitsCapped] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [needsNarrowing, setNeedsNarrowing] = useState(false);
  const [searchTermMode, setSearchTermMode] = useState<"all" | "any" | "none" | undefined>();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState(false);

  // The pass identity. Everything that should re-run the search bumps it; adopting the
  // server's seed deliberately does not, which is what keeps the opening load to ONE
  // request instead of "fetch seed, adopt, notice filters changed, fetch again".
  const [nonce, setNonce] = useState(0);

  // Read inside the effect and inside `loadMore` without making either depend on them.
  // Every one of these is written on the render that changes it, so a callback holding a
  // ref reads the value the pass was triggered by rather than a stale closure copy.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const targetRef = useRef(target);
  targetRef.current = target;
  const queryTextRef = useRef(queryText);
  queryTextRef.current = queryText;
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const pageRef = useRef(0);
  const requestSeq = useRef(0);

  // Stable by construction (empty dep list, everything read through refs) so that it can
  // never itself become the reason a pass re-fires.
  const buildRequest = useCallback(
    (page: number): REST_schema.ListingSourceCandidatesRequest => ({
      ...targetRef.current,
      // Before the seed lands there are no filters to send, and that absence IS the
      // request for a seed. Afterwards the whole set goes every time — an omitted key
      // means "re-seed this one", which would silently restore a filter she cleared.
      ...(filtersRef.current
        ? { filters: wireFiltersFromMatchFilters(filtersRef.current, queryTextRef.current.trim()) }
        : {}),
      sort: sortRef.current,
      page
    }),
    []
  );

  // Deps are the three things that define a NEW result set. `buildRequest` is
  // deliberately absent (it is stable by construction), and so is `filters`: a filter
  // edit announces itself by bumping `nonce`, which is what keeps "adopting the server's
  // seed" — a change to `filters` that must NOT re-search — expressible at all.
  useEffect(() => {
    const seq = ++requestSeq.current;
    pageRef.current = 0;
    setLoading(true);
    setError(false);

    const timer = setTimeout(async () => {
      try {
        const response = await apiRef.current.getListingSourceCandidates(buildRequest(0));
        if (seq !== requestSeq.current) return; // a newer pass started

        setCandidates(response.candidates ?? []);
        setTotalHits(response.totalHits ?? 0);
        setTotalHitsCapped(Boolean(response.totalHitsCapped));
        setHasMore(Boolean(response.hasMore));
        setNeedsNarrowing(Boolean(response.needsNarrowing));
        setSearchTermMode(response.searchTermMode);
        setSeed(response.seed ?? null);
        // Adopt the seed exactly once — from then on she owns the filters, and echoing
        // the server's `appliedFilters` back over them would undo an edit made while the
        // request was in flight.
        //
        // `seededFilterKeys` is captured HERE and nowhere else, for the same reason: once
        // the client starts sending the whole filter set, every key is caller-supplied by
        // definition and the server correctly reports none as seeded. The list describes
        // the SEED, so it is read from the one response that carried one.
        if (!filtersRef.current) {
          const adopted = matchFiltersFromWire(response.appliedFilters ?? {});
          filtersRef.current = adopted;
          setFiltersState(adopted);
          setSeedFilters(adopted);
          setSeededChips(seededChipIds(response.seededFilterKeys ?? []));
        }
        setLoading(false);
        setLoadedOnce(true);
      } catch (err) {
        console.error("ListingSourcePicker: candidate search failed", err);
        if (seq !== requestSeq.current) return;
        setLoading(false);
        setError(true);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, queryText, sort]);

  const setFilters = useCallback((patch: Partial<MatchFilters>) => {
    setFiltersState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      // Written through the ref too, so the pass this bump triggers reads the new value
      // even though the state update has not committed yet.
      filtersRef.current = next;
      return next;
    });
    setNonce((n) => n + 1);
  }, []);

  const resetToSeed = useCallback(() => {
    filtersRef.current = null;
    setFiltersState(null);
    setSeedFilters(null);
    setQueryText("");
    setSortState(DEFAULT_CANDIDATE_SORT);
    setNonce((n) => n + 1);
  }, []);

  const setSort = useCallback((next: SourceCandidateSort) => setSortState(next), []);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const seq = requestSeq.current;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    try {
      const response = await apiRef.current.getListingSourceCandidates(buildRequest(nextPage));
      // A filter change while this was in flight makes the appended page belong to a
      // different result set — drop it rather than mixing two searches into one list.
      if (seq !== requestSeq.current) return;
      pageRef.current = nextPage;
      setCandidates((prev) => {
        const seen = new Set(prev.map((candidate) => candidate.id));
        return [...prev, ...(response.candidates ?? []).filter((c) => !seen.has(c.id))];
      });
      setHasMore(Boolean(response.hasMore));
    } catch (err) {
      console.error("ListingSourcePicker: loading more candidates failed", err);
      // Deliberately not `error`: the list already on screen is valid, and blanking it
      // for a failed CONTINUATION would be worse than the button doing nothing.
      setHasMore(false);
    } finally {
      // Unconditionally: a newer pass makes the appended rows unwanted, not the button
      // permanently dead. Guarding this on `seq` left `loadingMore` stuck true whenever a
      // filter changed mid-continuation, and `loadMore` then early-returned forever.
      setLoadingMore(false);
    }
  }, [buildRequest, loadingMore]);

  // `setQueryText` and `setSort` are handed through as-is: the effect already lists both
  // as dependencies, so changing either re-runs the pass. Bumping `nonce` as well would
  // be harmless (React batches the two updates into one commit, so the effect still runs
  // once) but it would make a pass's trigger ambiguous, which is the one property this
  // hook is arranged around.
  return {
    filters,
    seedFilters,
    seededChips,
    seed,
    queryText,
    sort,
    candidates,
    totalHits,
    totalHitsCapped,
    hasMore,
    needsNarrowing,
    searchTermMode,
    loading,
    loadingMore,
    loadedOnce,
    error,
    setFilters,
    resetToSeed,
    setQueryText,
    setSort,
    loadMore: () => void loadMore(),
    retry
  };
}
